import { useState } from "react";
import { FaFilePdf, FaFileExcel, FaDownload } from "react-icons/fa";
import { downloadDocumentoByIdentificador } from "../../../../../services/documentoService";

export interface ArquivoDiretoItem {
  nomeArquivo?: string;
  identificador?: string;
  extensao?: string;
  dataUpload?: string | Date;
  [key: string]: unknown;
}

const getIconByExtension = (extensao: string) => {
  switch (extensao) {
    case "pdf":
      return <FaFilePdf className="text-danger" />;
    case "xlsx":
      return <FaFileExcel className="text-success" />;
    default:
      return <FaFilePdf />;
  }
};

const getNomeBase = (nomeArquivo = "") => {
  return nomeArquivo
    .toLowerCase()
    .replace(/-\w{8}-\w{4}-\w{4}-\w{4}-\w{12}/, "")
    .replace(/-v\d+/i, "")
    .trim();
};

interface UploadSectionAnalysisProps {
  arquivosDiretos?: ArquivoDiretoItem[];
}

const UploadSectionAnalysis = ({ arquivosDiretos = [] }: UploadSectionAnalysisProps) => {
  const [loadingIds, setLoadingIds] = useState<Set<string>>(() => new Set());

  const startLoading = (id: string) => {
    setLoadingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const stopLoading = (id: string) => {
    setLoadingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  if (!arquivosDiretos.length) {
    return <p className="text-muted small">Nenhum arquivo enviado.</p>;
  }

  const agrupado: Record<string, ArquivoDiretoItem[]> = {};
  arquivosDiretos.forEach((arquivo) => {
    const nomeBase = getNomeBase(arquivo.nomeArquivo ?? "");
    if (!agrupado[nomeBase]) agrupado[nomeBase] = [];
    agrupado[nomeBase].push(arquivo);
  });

  const arquivosMaisRecentes = Object.values(agrupado).map((lista: ArquivoDiretoItem[]) =>
    lista.reduce((a, b) =>
      new Date(a.dataUpload ?? 0) > new Date(b.dataUpload ?? 0) ? a : b,
    ),
  );

  const handleClick = async (file: ArquivoDiretoItem) => {
    if (!file?.identificador) return;
    if (loadingIds.has(file.identificador)) return;

    startLoading(file.identificador);
    try {
      await downloadDocumentoByIdentificador(
        file.identificador,
        file.nomeArquivo,
      );
    } catch (e) {
      console.error("Erro no download/abertura:", e);
    } finally {
      stopLoading(file.identificador);
    }
  };

  return (
    <div className="upload-section-analysis mt-2">
      {arquivosMaisRecentes.map((file) => {
        const fileId = file.identificador ?? "";
        const isLoading = loadingIds.has(fileId);
        return (
          <div
            key={fileId}
            className="d-flex align-items-center mb-2 gap-2"
          >
            {getIconByExtension(file.extensao ?? "")}
            <span className="small">{file.nomeArquivo ?? ""}</span>

            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => handleClick(file)}
              disabled={isLoading}
              title={isLoading ? "Processando..." : "Abrir/baixar arquivo"}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="visually-hidden">Carregando...</span>
                  Processando...
                </>
              ) : (
                <>
                  <FaDownload />
                  Baixar
                </>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default UploadSectionAnalysis;
